import { Grid, Link, Typography } from "@mui/material";
import { Buffer } from "buffer";
import * as React from "react";
import { useLocation, Link as RouterLink } from "react-router-dom";
import { initialAppState } from "../../common/reducers/appState";
import { apiPost, fetchData } from "../../common/services/ApiClient";
import {
  appHeaderTitle,
  getCookiebyName,
} from "../../common/services/Utilities";
import { ApiBaseUrl, AppUrl } from "../../config";
import BackButton from "../../containers/BackButton";
import Breadcrumbs from "../../containers/Breadcrumbs";
import CloudFoundryBanner from "../../containers/CloudFoundryBanner";
import EditColumn from "../../containers/EditColumn";
import PeerGroupStepTwo from "../../containers/PeerGroupStepTwo";
import PeerGroups from "../../containers/PeerGroups";
import { useInitialPageLoad } from "../../hooks/useInitialPageLoad";
import { KeyValue, Query } from "../../interfaces";
import { AppBody } from "../AppBody";
import { rangeSplitDateFormat } from "../FinancialReporting/FinancialReportingUtilities";
import AddButton from "./AddButton";
import peerGroupCustomCompareData from "./PeerGroupCustomCompareData";
import { subOptionLookup } from "./PeerGroupUtilities";

interface Props {
  appState: Query;
  updateUi: (
    uiField: string,
    uiValue: KeyValue[] | string[] | string | KeyValue,
  ) => void;
  resetState: (appState: Query) => void;
}

export default function CustomizedComparison({
  appState,
  updateUi,
  resetState,
}: Props) {
  const [initialLoad, setInitialLoad] = React.useState(true);
  const [individualState, setIndividualState] = React.useState([]);
  const user = getCookiebyName("uuid") || undefined;
  const location = useLocation();
  React.useEffect(() => {
    if (initialLoad && !location.search) {
      setInitialLoad(false);
      resetState(initialAppState.peerGroup);
    }
  }, [appState, initialLoad, resetState, location.search]);
  useInitialPageLoad("customizedPeerGroup");

  const introParagraph = (
    <>
      {appHeaderTitle("customizedPeerGroup")}
      <Typography variant="h2">Customized Comparison Reports</Typography>
      <Typography
        variant="body2"
        color="primary.main"
        style={{ paddingRight: "10px" }}
      >
        Compare the same financial data between individual institutions, peer
        groups, or bank holding companies in any combination. You can also
        choose what data you wish to see compared between the institutions or
        peer groups you’ve selected. If you need assistance, please see the{" "}
        <RouterLink to={"/help?helpTopic=tutorials"}>Help Section</RouterLink>{" "}
        for tutorials to get you started.
      </Typography>
      <Typography
        variant="body2"
        color="primary.main"
        id="peer-group-feedback-link"
      >
        Provide{" "}
        <Link
          href={
            "https://ask.fdic.gov/fdicinformationandsupportcenter/s/fdicdirform?app=PeerGroupCustomized"
          }
        >
          feedback or submit a question
        </Link>{" "}
        about this page.
      </Typography>
    </>
  );

  const customState = appState.ui.customPeerGroups;
  React.useEffect(() => {
    if (
      appState.ui.individualCert &&
      appState.ui.individualCert?.length > 0 &&
      appState.ui.individualCert?.length !== individualState?.length
    ) {
      const certsArray = appState.ui.individualCert.map(
        (certData: KeyValue) => certData.value,
      );
      let isMounted = true;
      const institutionNamePost = apiPost({
        limit: 10000,
        offset: 0,
        filters: `CERT:(${certsArray.join(" OR ")})`,
      });
      (async () => {
        await fetchData(`${ApiBaseUrl()}/institutions`, institutionNamePost)
          .then((response) => {
            if (isMounted) {
              const newState = appState.ui.individualCert.map(
                (data: KeyValue) => {
                  const establishedDate =
                    response.data?.ESTYMD &&
                    response.data
                      .find(
                        (res: KeyValue) => res.CERT.toString() === data.value,
                      )
                      .ESTYMD?.split("/");
                  const establishedYear =
                    establishedDate?.length > 0 ? establishedDate[2] : "1992";
                  const estaglishedMD =
                    establishedDate?.length > 0
                      ? `${establishedDate[0]}${establishedDate[1]}`
                      : "0331";

                  const earliestDataQtr = ["1231", "0930", "0630", "0331"]
                    .map((quarter: string) => {
                      return Number(estaglishedMD) <= Number(quarter)
                        ? quarter
                        : "";
                    })
                    .filter((val) => val !== "")
                    .slice(-1)[0];

                  return {
                    ...data,
                    bankName: response.data.find(
                      (res: KeyValue) => res.CERT.toString() === data.value,
                    ).NAME,
                    maxRepDate: rangeSplitDateFormat(
                      response.data.find(
                        (res: KeyValue) => res.CERT.toString() === data.value,
                      ).REPDTE,
                    ),
                    minRepDate: `${establishedYear}${earliestDataQtr}`,
                  };
                },
              );
              setIndividualState(newState);
            }
          })
          .catch((error: Error) => {
            console.error(`${error.name} : ${error.message}`);
          })
          .finally(() => (isMounted = false));
      })();
    }
  });

  const peerGroupData = peerGroupCustomCompareData(
    appState.ui.peerGroups,
    customState,
    individualState,
    undefined,
    appState.ui.standardGroupSearchPills,
  );

  const stepTwoData = peerGroupData.map((data: KeyValue) => {
    const savedAppState = data?.saved_state
      ? JSON.parse(Buffer.from(data.saved_state, "base64").toString("utf-8"))
      : undefined;

    return {
      filterCriteria: data.filter_criteria,
      longName: data.name,
      title: data.peerGroupTitle,
      maxReportDate: data.maxReportDate,
      minReportDate: data.minReportDate,
      appState: savedAppState,
      shortName: data?.short_name,
      is_standard:
        data?.short_name && subOptionLookup.hasOwnProperty(data.short_name),
    };
  });

  const storedAppStateUi = localStorage.getItem("appStateUi");
  React.useEffect(() => {
    if (storedAppStateUi) {
      const updatedAppStateUi = JSON.parse(storedAppStateUi);
      if (updatedAppStateUi?.customPeerGroups) {
        updateUi("customPeerGroups", updatedAppStateUi.customPeerGroups);
      }
      if (updatedAppStateUi?.individualCert) {
        updateUi("individualCert", updatedAppStateUi.individualCert);
      }
      if (updatedAppStateUi?.standardGroupSearchPills) {
        updateUi(
          "standardGroupSearchPills",
          updatedAppStateUi.standardGroupSearchPills,
        );
      }
    }
  }, [storedAppStateUi, updateUi]);
  return (
    <>
      <Grid container spacing={2}>
        <Grid size={12}>
          <Breadcrumbs currentPageBreadCrumbTitle="Customized Comparisons" />
        </Grid>
      </Grid>
      <CloudFoundryBanner />
      <AppBody
        login
        appState={appState}
        introText={introParagraph}
        backButton={
          <BackButton
            app="FinancialReporting"
            buttonLabel="Back to Financial Reporting"
            variant="dark"
          />
        }
      >
        <Typography component="div" style={{ paddingTop: "15px" }}>
          {appState.ui.pgcStep === "step3" && <EditColumn customComparison />}
          {appState.ui.peerGroups?.length > 0 &&
            (appState.ui.pgcStep !== "step3" ? (
              <PeerGroups
                userId={user}
                data={peerGroupData}
                customCompare
                groups={appState.ui.peerGroups}
              />
            ) : (
              <PeerGroupStepTwo stepTwoCustomData={stepTwoData} />
            ))}
          {appState.ui.peerGroups?.length === 0 && (
            <AddButton
              title={"Add institutions or peer groups for comparison"}
              path={`${AppUrl("PeerGroup")}/customized/search`}
              onClick={() => {
                //resets the selections on initial load of customized comparisons
                updateUi("customPeerGroups", []);
                updateUi("standardGroupSearchPills", []);
                updateUi("standardPeerGroups", []);
                updateUi("subOptionsSelected", {});
                updateUi("individualCert", []);
                localStorage.removeItem("appStateUi");
              }}
            />
          )}
        </Typography>
      </AppBody>
    </>
  );
}
